/*
 * Bundle & Save tally.
 * - Counts units of eligible products in the real cart (refreshes on every cart update).
 * - Tier and savings come from Shopify's automatic discounts; this only displays them.
 * - [data-bundle-add] buttons add one unit straight to the cart, refresh the cart drawer
 *   in the background (no forced open) and show a short confirmation.
 * - "Review bundle" opens the existing cart drawer and highlights the bundle items.
 */
if (!customElements.get('bundle-tally')) {
  class BundleTally extends HTMLElement {
    connectedCallback() {
      this.ids = new Set((this.dataset.ids || '').split(',').filter(Boolean));
      this.tiers = (this.dataset.tiers || '')
        .split(',')
        .map((pair) => pair.split(':').map((n) => Number(String(n).trim())))
        .filter(([q, p]) => q > 0 && p >= 0)
        .sort((a, b) => a[0] - b[0]);
      this.max = this.tiers.length ? this.tiers[this.tiers.length - 1][0] : 4;
      this.count = Number(this.dataset.count || 0);
      this.saved = Number(this.dataset.saved || 0);
      this.cartItems = [];

      this.toast = this.querySelector('.bundle-tally__toast');
      this.toggle = this.querySelector('.bundle-tally__toggle');
      this.reviewButton = this.querySelector('[data-bt-review]');
      this.markers = this.querySelector('[data-bt-markers]');
      this.markers.replaceChildren();
      for (let i = 1; i <= this.max; i++) {
        const marker = document.createElement('li');
        const tier = this.tiers.find(([quantity]) => quantity === i);
        marker.dataset.tier = tier ? `${tier[1]}%` : '';
        marker.dataset.unit = String(i);
        if (tier) {
          const label = document.createElement('span');
          label.className = 'bundle-tally__marker-label';
          label.textContent = this.fill(this.dataset.lPct, { percent: tier[1] });
          marker.appendChild(label);
        }
        this.markers.appendChild(marker);
      }

      // Stay a small pill until an eligible item is added, then pop open and shrink back after a pause.
      this.tuckSeconds = Number(this.dataset.tuck || 0);
      this.setCollapsed(true);
      this.toggle.addEventListener('click', () => {
        clearTimeout(this.tuckTimer);
        this.autoOpened = false;
        this.setCollapsed(!this.classList.contains('is-collapsed'));
      });
      this.addEventListener('mouseenter', () => clearTimeout(this.tuckTimer));
      this.addEventListener('focusin', () => clearTimeout(this.tuckTimer));
      this.addEventListener('mouseleave', () => this.scheduleTuck());
      this.addEventListener('focusout', (event) => {
        if (!this.contains(event.relatedTarget)) this.scheduleTuck();
      });
      if (this.reviewButton) this.reviewButton.addEventListener('click', () => this.review());
      this.cartTrigger = this.querySelector('[data-bt-open-cart]');
      if (this.cartTrigger) {
        this.cartTrigger.addEventListener('click', () => this.review(this.cartTrigger));
        this.cartTrigger.addEventListener('keydown', (event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            this.review(this.cartTrigger);
          }
        });
      }

      this.onAddClick = this.onAddClick.bind(this);
      document.addEventListener('click', this.onAddClick);

      this.render();
      this.refresh();
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.refresh());
      }
      // Some cart integrations do not publish the theme's cartUpdate event.
      // Keep progress in sync with the real cart, including adds and removals.
      this.onCartInteraction = () => {
        clearTimeout(this.cartRefreshTimer);
        this.cartRefreshTimer = setTimeout(() => this.refresh(), 800);
      };
      document.addEventListener('submit', this.onCartInteraction);
      document.addEventListener('change', this.onCartInteraction);
      this.cartPoll = setInterval(() => {
        if (!document.hidden) this.refresh();
      }, 3000);
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onAddClick);
      if (this.unsubscribe) this.unsubscribe();
      document.removeEventListener('submit', this.onCartInteraction);
      document.removeEventListener('change', this.onCartInteraction);
      clearTimeout(this.cartRefreshTimer);
      clearInterval(this.cartPoll);
      clearTimeout(this.tuckTimer);
    }

    fill(template, values) {
      return (template || '').replace(/\[(\w+)\]/g, (m, key) => (key in values ? values[key] : m));
    }

    money(cents) {
      try {
        return new Intl.NumberFormat(document.documentElement.lang || undefined, {
          style: 'currency',
          currency: this.dataset.currency || 'USD',
        }).format(cents / 100);
      } catch (e) {
        return '$' + (cents / 100).toFixed(2);
      }
    }

    percentFor(count) {
      let pct = 0;
      this.tiers.forEach(([q, p]) => {
        if (count >= q) pct = p;
      });
      return pct;
    }

    nextTier(count) {
      return this.tiers.find(([q]) => q > count);
    }

    statusText() {
      const shown = Math.min(this.count, this.max);
      const pct = this.percentFor(this.count);
      return {
        count: this.fill(this.dataset.lCount, { count: shown, max: this.max }),
        pct: this.fill(this.dataset.lPct, { percent: pct }),
        pctValue: pct,
      };
    }

    render() {
      const d = this.dataset;
      const status = this.statusText();
      const next = this.nextTier(this.count);
      const full = !next;

      this.querySelector('[data-bt-count]').textContent = status.count;
      this.querySelector('[data-bt-pct]').textContent = status.pct;
      this.querySelector('[data-bt-mini]').textContent = `${Math.min(this.count, this.max)}/${this.max}`;

      const hint = this.querySelector('[data-bt-hint]');
      const note = this.querySelector('[data-bt-note]');
      if (full) {
        hint.textContent = this.fill(d.lFullTitle, { percent: status.pctValue, count: this.count });
        note.textContent = this.fill(d.lFullText, { percent: status.pctValue, count: this.count });
        note.hidden = !note.textContent;
      } else {
        const need = next[0] - this.count;
        hint.textContent = this.fill(this.count === 0 ? d.lStart : d.lNext, { need, percent: next[1] });
        note.hidden = true;
      }

      const units = this.units || [];
      this.markers.querySelectorAll('li').forEach((li, i) => {
        const filled = i < this.count;
        li.classList.toggle('is-filled', filled);
        const unit = units[i];
        let thumb = li.querySelector('.bundle-tally__thumb');
        if (filled && unit && unit.image) {
          let src = unit.image;
          try {
            const url = new URL(unit.image, window.location.origin);
            url.searchParams.set('width', '160');
            src = url.toString();
          } catch (e) { /* use the original image URL */ }
          if (!thumb) {
            thumb = document.createElement('img');
            thumb.className = 'bundle-tally__thumb';
            thumb.alt = '';
            thumb.loading = 'lazy';
            li.prepend(thumb);
          }
          if (thumb.dataset.src !== src) {
            thumb.dataset.src = src;
            thumb.src = src;
          }
          li.classList.add('has-thumb');
        } else {
          if (thumb) thumb.remove();
          li.classList.remove('has-thumb');
        }
      });
      // Celebrate the moment the last box fills (not on every page load once already full).
      if (full && this.wasFull === false) {
        this.classList.remove('is-celebrating');
        void this.offsetWidth;
        this.classList.add('is-celebrating');
        clearTimeout(this.celebrateTimer);
        this.celebrateTimer = setTimeout(() => this.classList.remove('is-celebrating'), 2400);
        this.showToast(hint.textContent);
      }
      this.wasFull = full;
      this.classList.toggle('is-full', full);
      this.classList.toggle('is-empty', this.count === 0);

      const saved = this.querySelector('[data-bt-saved]');
      saved.hidden = this.saved <= 0;
      saved.textContent = this.saved > 0 ? this.fill(d.lSaved, { amount: this.money(this.saved) }) : '';
    }

    async refresh() {
      if (this.refreshPending) return;
      this.refreshPending = true;
      try {
        const response = await fetch(`${this.dataset.cartUrl || '/cart'}.js`, {
          headers: { Accept: 'application/json' },
          cache: 'no-store',
        });
        if (!response.ok) return;
        const cart = await response.json();
        this.applyCart(cart);
      } catch (e) {
        /* keep the last known state */
      } finally {
        this.refreshPending = false;
      }
    }

    applyCart(cart) {
      const prefix = this.dataset.prefix || '';
      let count = 0;
      let saved = 0;
      this.units = [];
      this.cartItems = cart.items || [];
      this.cartItems.forEach((item) => {
        if (this.ids.has(String(item.product_id)) && !item.selling_plan_allocation) {
          count += item.quantity;
          for (let q = 0; q < item.quantity; q++) {
            this.units.push({ image: item.image || (item.featured_image && item.featured_image.url) || '', title: item.product_title || item.title || '' });
          }
        }
        (item.line_level_discount_allocations || []).forEach((allocation) => {
          const title = (allocation.discount_application && allocation.discount_application.title) || '';
          if (prefix && title.indexOf(prefix) === 0) saved += Number(allocation.amount || 0);
        });
      });
      const added = this.synced && count > this.count;
      this.synced = true;
      this.count = count;
      this.saved = saved;
      this.render();
      if (added) this.popOpen();
    }

    popOpen() {
      if (this.classList.contains('is-collapsed')) {
        this.setCollapsed(false);
        this.autoOpened = true;
      }
      this.scheduleTuck();
    }

    scheduleTuck() {
      clearTimeout(this.tuckTimer);
      if (!this.autoOpened || !this.tuckSeconds || this.classList.contains('is-collapsed')) return;
      this.tuckTimer = setTimeout(() => {
        if (this.matches(':hover') || this.contains(document.activeElement)) return;
        this.setCollapsed(true);
      }, this.tuckSeconds * 1000);
    }

    setCollapsed(collapsed) {
      this.classList.toggle('is-collapsed', collapsed);
      this.toggle.setAttribute('aria-expanded', String(!collapsed));
      this.toggle.setAttribute('aria-label', collapsed ? this.dataset.lExpand : this.dataset.lCollapse);
      if (collapsed) this.autoOpened = false;
    }

    showToast(text) {
      clearTimeout(this.toastTimer);
      this.toast.textContent = text;
      this.toast.hidden = false;
      requestAnimationFrame(() => this.toast.classList.add('is-visible'));
      this.toastTimer = setTimeout(() => {
        this.toast.classList.remove('is-visible');
        setTimeout(() => (this.toast.hidden = true), 250);
      }, 2600);
    }

    variantFor(button) {
      const item = button.closest('li') || button.parentElement;
      const select = item && item.querySelector('[data-variant-select]');
      if (select) return select.value ? Number(select.value) : null;
      const fixed = item && item.querySelector('[data-variant-fixed]');
      return fixed ? Number(fixed.value) : null;
    }

    async onAddClick(event) {
      const button = event.target.closest('[data-bundle-add]');
      if (!button || button.disabled || button.getAttribute('aria-busy') === 'true') return;
      event.preventDefault();

      const variantId = this.variantFor(button);
      if (!variantId) {
        const select = (button.closest('li') || document).querySelector('[data-variant-select]');
        if (select) {
          select.focus();
          select.classList.add('is-invalid');
          setTimeout(() => select.classList.remove('is-invalid'), 1600);
        }
        return;
      }

      const label = button.textContent;
      button.setAttribute('aria-busy', 'true');
      button.classList.add('loading');

      const drawer = document.querySelector('cart-drawer');
      const sections = drawer && drawer.getSectionsToRender ? drawer.getSectionsToRender() : [];
      const addUrl = (window.routes && window.routes.cart_add_url) || '/cart/add';

      try {
        const response = await fetch(`${addUrl}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: [{ id: variantId, quantity: 1 }],
            sections: sections.map((s) => s.id),
            sections_url: window.location.pathname,
          }),
        });
        const data = await response.json();
        if (!response.ok || data.status) {
          this.showToast(data.description || data.message || 'Could not add this item.');
          return;
        }

        if (drawer && data.sections) {
          sections.forEach((section) => {
            const el = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
            const html = data.sections[section.id];
            if (el && html) el.innerHTML = drawer.getSectionInnerHTML(html, section.selector);
          });
          drawer.classList.remove('is-empty');
          const inner = drawer.querySelector('.drawer__inner');
          if (inner) inner.classList.remove('is-empty');
        }

        await this.refresh();
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'bundle-tally', productVariantId: variantId });
        }

        const status = this.statusText();
        const title = button.dataset.title ? `${button.dataset.title}. ` : '';
        this.showToast(`${this.dataset.lAdded}: ${title}${status.count} · ${status.pct}`);
      } catch (e) {
        this.showToast('Could not add this item. Please try again.');
      } finally {
        button.removeAttribute('aria-busy');
        button.classList.remove('loading');
        button.textContent = label;
      }
    }

    review(opener) {
      const drawer = document.querySelector('cart-drawer');
      if (!drawer) {
        window.location.href = this.dataset.cartUrl || '/cart';
        return;
      }
      drawer.open(opener || this.reviewButton);
      setTimeout(() => {
        let first = null;
        this.cartItems.forEach((item, index) => {
          if (!this.ids.has(String(item.product_id))) return;
          const row = document.getElementById(`CartDrawer-Item-${index + 1}`);
          if (!row) return;
          row.classList.add('is-bundle-item', 'is-bundle-focus');
          setTimeout(() => row.classList.remove('is-bundle-focus'), 2200);
          if (!first) first = row;
        });
        if (first) first.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }, 350);
    }
  }
  customElements.define('bundle-tally', BundleTally);
}
